import { SyntheticEvent, useCallback, useMemo } from 'react';
import { Autocomplete, TextField, Chip, Box } from '@mui/material';
import { TextFieldProps, fieldToTextField } from 'formik-mui';
import { IManager } from '@sr/dto';
import { useReadOnlyRemoteCollection } from 'utils/remote-collection.hook';
import { fetchCollectionReq } from 'shared/api/axios';
import { mergeAutocompleteInputSx } from 'shared/ui/autocomplete-input-sx';

export const fetchSubordinatesDropdown = async (managerId?: number | null) => {
  const query = managerId ? `?managerId=${managerId}` : '';
  return fetchCollectionReq<IManager>(`tasks/employee-dropdown${query}`);
};

type EmployeePickerFieldProps = {
  clearable?: boolean;
  size?: 'small' | 'medium' | undefined;
  managerId?: number | null;
  multiple?: boolean;
} & TextFieldProps;

export const EmployeePickerField = (props: EmployeePickerFieldProps) => {
  const {
    form: { setFieldValue, setFieldTouched, setFieldError },
    field: { name, value },
    size = undefined,
    multiple = false,
    clearable = false,
    disabled = false,
    label,
  } = props;

  const { items: employees, isLoading: fetching } = useReadOnlyRemoteCollection<IManager>({
    fetchHandler: useCallback(() => fetchSubordinatesDropdown(props.managerId ?? null), [props.managerId]),
  });

  const safeValue = useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value : [];
    }
    return value;
  }, [multiple, value]);

  const selectedEmployees = useMemo(() => {
    if (multiple) {
      const ids = safeValue as number[];
      return employees.filter((emp) => ids.includes(emp.id));
    } else {
      const id = typeof value === 'number' ? value : null;
      return id !== null ? employees.find((emp) => emp.id === id) || null : null;
    }
  }, [employees, safeValue, value, multiple]);

  const onChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, newValue: IManager | IManager[] | null) => {
      if (multiple) {
        const selectedIds = Array.isArray(newValue) ? newValue.map((item) => item.id) : [];
        setFieldValue(name, selectedIds, true);
        setFieldTouched(name, true, false);
        setFieldError(name, undefined);
      } else {
        if (!newValue && !clearable) return;
        const id = newValue && typeof newValue === 'object' && 'id' in newValue ? newValue.id : null;
        setFieldValue(name, id, true);
        setFieldTouched(name, true, false);
        setFieldError(name, undefined);
      }
    },
    [multiple, clearable, setFieldValue, setFieldTouched, setFieldError, name],
  );

  const autocompleteValue = multiple
    ? (selectedEmployees as IManager[])
    : (selectedEmployees as IManager | null);

  const getOptionLabel = (option: IManager) => `${option.firstName} ${option.secondName || ''}`.trim();

  const renderTags = useCallback(
    (value: IManager[], getTagProps: (arg: { index: number }) => Record<string, unknown>) => {
      const displayed = value.slice(0, 2);
      const remaining = value.length - displayed.length;

      return (
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', overflow: 'hidden' }}>
          {displayed.map((option, index) => (
            <Chip
              key={option.id}
              label={getOptionLabel(option)}
              {...getTagProps({ index })}
              sx={{
                maxWidth: 120,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          ))}
          {remaining > 0 && <Chip label={`+${remaining}`} sx={{ ml: 0.5 }} size="small" />}
        </Box>
      );
    },
    [getOptionLabel],
  );

  const hasValue = multiple
    ? Array.isArray(safeValue) && safeValue.length > 0
    : value !== null && value !== undefined && value !== '';

  const placeholderText = hasValue
    ? ''
    : multiple
      ? 'Начните набирать имя...'
      : 'Начните набирать имя...';

  return (
    <Autocomplete
      fullWidth
      multiple={multiple}
      value={autocompleteValue}
      options={employees}
      disabled={disabled}
      disableClearable={!clearable}
      size={size}
      onChange={onChange}
      isOptionEqualToValue={(option, val) => {
        if (!val) return false;
        return option.id === (val as IManager).id;
      }}
      getOptionLabel={getOptionLabel}
      loading={fetching}
      loadingText="Загрузка..."
      noOptionsText="Ничего не найдено"
      renderTags={multiple ? renderTags : undefined}
      renderInput={(params) => {
        const { sx, ...restFieldProps } = fieldToTextField(props);
        return (
          <TextField
            {...restFieldProps}
            name={name}
            {...params}
            disabled={disabled}
            label={label ?? 'Сотрудник'}
            placeholder={placeholderText}
            sx={mergeAutocompleteInputSx(sx)}
            InputProps={{
              ...params.InputProps,
              sx: {
                '& .MuiInputBase-input': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              },
            }}
            error={props.form.touched[name] && Boolean(props.form.errors[name])}
            helperText={props.form.touched[name] && props.form.errors[name] ? String(props.form.errors[name]) : undefined}
          />
        );
      }}
    />
  );
};